<template>
  <div class="assistant-thread" data-testid="assistant-chat">
    <!-- Message list (presentational bubbles) -->
    <div
      ref="messagesContainer"
      class="assistant-messages"
      data-testid="assistant-messages"
      @scroll.passive="onMessagesScroll"
    >
      <!-- Empty state: no chat selected -->
      <div
        v-if="activeChatId === null"
        class="assistant-empty-state"
        data-testid="assistant-empty"
      >
        <i class="pi pi-sparkles assistant-empty-icon" />
        <p class="assistant-empty-greeting">{{ t('Olá! Como posso ajudar?') }}</p>
        <div class="assistant-suggestions">
          <chip
            v-for="(prompt, idx) in suggestedPrompts"
            :key="idx"
            class="assistant-suggestion-chip"
            :data-testid="`assistant-suggested-prompt`"
            @click="fillDraft(prompt)"
          >
            {{ t(prompt) }}
          </chip>
        </div>
      </div>

      <!-- Empty state: chat selected but no messages -->
      <div
        v-else-if="messages.length === 0"
        class="assistant-empty-state"
        data-testid="assistant-empty"
      >
        <i class="pi pi-sparkles assistant-empty-icon" />
        <p class="assistant-empty-greeting">{{ t('Pergunte sobre clientes, ordens de serviço, finanças e mais') }}</p>
        <div class="assistant-suggestions">
          <chip
            v-for="(prompt, idx) in suggestedPrompts"
            :key="idx"
            class="assistant-suggestion-chip"
            :data-testid="`assistant-suggested-prompt`"
            @click="fillDraft(prompt)"
          >
            {{ t(prompt) }}
          </chip>
        </div>
      </div>

      <div
        v-for="(message, index) in messages"
        :key="message.id"
        class="assistant-bubble-wrapper"
        :class="message.role === 'user' ? 'assistant-bubble-wrapper-user' : 'assistant-bubble-wrapper-assistant'"
      >
        <div
          class="assistant-bubble"
          :class="message.role === 'user' ? 'assistant-bubble-user' : 'assistant-bubble-assistant'"
          :data-testid="`assistant-message-${index}`"
          :data-testid-role="message.role"
        >
          <!-- User messages: plain text interpolation (no injection surface) -->
          <span
            v-if="message.role === 'user'"
            class="assistant-bubble-text"
            data-testid="assistant-message-user"
          >{{ message.content }}</span>

          <!-- Assistant messages (streamed): chronological reason/output timeline.
               reason segments are per-message collapsible (▸ Raciocínio expander);
               output segments are each their own markdown block (stacked, no run-on). -->
          <template v-if="message.role === 'assistant' && message.segments && message.segments.length">
            <!-- Per-message reasoning expander — only when reason segments exist -->
            <template v-if="message.segments.some((s) => s.type === 'reason')">
              <button
                class="assistant-reason-toggle"
                :data-testid="`assistant-reason-toggle-${index}`"
                @click="toggleReason(index)"
              >
                {{ expandedReasonIndex === index ? '▾' : '▸' }} {{ t('Raciocínio') }}
              </button>
              <div
                v-if="expandedReasonIndex === index"
                class="assistant-reason-expanded"
              >
                <template v-for="(seg, si) in message.segments" :key="si">
                  <div
                    v-if="seg.type === 'reason'"
                    class="assistant-reason"
                    data-testid="assistant-message-reason"
                  >{{ seg.text }}</div>
                </template>
              </div>
            </template>

            <!-- Output segments -->
            <template v-for="(seg, si) in message.segments" :key="si">
              <div
                v-if="seg.type === 'output'"
                class="assistant-bubble-text assistant-bubble-markdown assistant-output-segment"
                data-testid="assistant-message-assistant"
                v-html="renderMarkdownSanitizado(seg.text)"
              ></div>
            </template>
          </template>

          <!-- Assistant messages (persisted/historical): no timeline — render content. -->
          <div
            v-else-if="message.role === 'assistant'"
            class="assistant-bubble-text assistant-bubble-markdown"
            data-testid="assistant-message-assistant"
            v-html="renderMarkdownSanitizado(message.content)"
          ></div>

          <!-- Streaming progress indicator: shown while the SSE stream is open. -->
          <span
            v-if="message.streaming"
            class="assistant-streaming-indicator"
            data-testid="assistant-streaming-indicator"
            aria-label="pensando…"
          >▋</span>

          <!-- Tool-call progress: shown between tool_start and tool_result frames. -->
          <span
            v-if="message.toolActivity"
            class="assistant-tool-indicator"
            :data-testid="`assistant-tool-indicator`"
          >{{ t('chamando') }} {{ displayToolName(message.toolActivity.name) }}…</span>
        </div>

        <!-- AssistantDiagnosticsChip — Full-only, assistant turns, non-null diagnostics (D6). -->
        <AssistantDiagnosticsChip
          v-if="isFull && message.role === 'assistant' && message.diagnostics !== null"
          :diagnostics="message.diagnostics"
        />
      </div>
    </div>

    <!-- Error surface -->
    <Message
      v-if="error"
      severity="error"
      :closable="false"
      class="assistant-error"
      data-testid="assistant-error"
    >
      {{ error }}
    </Message>

    <!-- Status line: shown during streaming or tool activity (above composer) -->
    <div
      v-if="isStreaming"
      class="assistant-status-line"
      data-testid="assistant-status-line"
    >
      <span v-if="activeToolName" class="assistant-status-text">
        {{ t('chamando') }} {{ displayToolName(activeToolName) }}…
      </span>
      <span v-else class="assistant-status-text assistant-status-thinking">
        <span class="assistant-typing-dot" />
        <span class="assistant-typing-dot" />
        <span class="assistant-typing-dot" />
        {{ t('pensando…') }}
      </span>
    </div>

    <!-- 402 Insufficient credits banner (above composer, below status) -->
    <div
      v-if="insufficient"
      class="assistant-insufficient-banner"
      data-testid="assistant-insufficient-banner"
    >
      <span class="assistant-insufficient-text">{{ t('Créditos insuficientes para esta franquia.') }}</span>
      <!-- Full/Franqueado: can switch franchises to pick one with balance -->
      <Button
        v-if="isFranqueadoOrFull"
        class="assistant-insufficient-cta"
        data-testid="assistant-insufficient-cta"
        :label="t('Trocar franquia')"
        size="small"
        severity="danger"
        outlined
        @click="openFranchiseModal"
      />
      <!-- Other groups (Gerente, etc.): inert stub — D7 notifications deferred -->
      <span
        v-else
        v-tooltip.top="t('Notificações ao franqueado chegam em breve')"
        data-testid="assistant-402-request-topup-wrapper"
      >
        <Button
          class="assistant-insufficient-cta"
          data-testid="assistant-402-request-topup"
          :label="t('Solicitar recarga ao franqueado')"
          size="small"
          severity="secondary"
          :disabled="true"
          @click="onRequestTopupStubClick"
        />
      </span>
    </div>

    <!-- Input row -->
    <div class="assistant-input-row">
      <div class="assistant-input-field">
        <input-text
          ref="inputRef"
          data-testid="assistant-input"
          elem-id="assistant_thread_input"
          :placeholder="t('Pergunte sobre clientes, ordens de serviço, finanças e mais')"
          :disabled="loading || activeChatId === null"
          :fluid="true"
          v-model="draft"
          @keyup.enter="submit"
        >
        </input-text>
      </div>
      <Button
        data-testid="assistant-send-btn"
        :label="t('Enviar')"
        icon="pi pi-send"
        severity="primary"
        :loading="loading"
        :disabled="loading || activeChatId === null || draft.trim().length === 0"
        @click="submit"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import type { StreamingChatMessageView } from '../../composables/useAgentChat';
import { renderMarkdownSanitizado } from '../../utils/renderMarkdownSanitizado';
import { eventBus } from '../../eventBus';
import { log } from '../../utils/DebugLogger';
import AssistantDiagnosticsChip from './AssistantDiagnosticsChip.vue';
import permissionsService from '../../services/permissionsService';

const props = defineProps<{
  messages: StreamingChatMessageView[];
  loading: boolean;
  error: string | null;
  activeChatId: number | null;
  /** When true, a 402 banner is shown with a 'Trocar franquia' CTA. */
  insufficient?: boolean;
}>();

const emit = defineEmits<{
  send: [text: string];
}>();

const { t } = useI18n();

/** True when the signed-in user belongs to the Full group (D6: chip gating). */
const isFull = computed<boolean>(() => permissionsService.isUserInGroups(['Full']));

/**
 * True when the signed-in user is Full OR Franqueado.
 * Used to branch the 402 banner CTA: Full/Franqueado can switch franchises;
 * other groups (Gerente, etc.) get the inert D7 stub.
 */
const isFranqueadoOrFull = computed<boolean>(() =>
  permissionsService.isUserInGroups(['Full', 'Franqueado']),
);

const draft = ref<string>('');
const messagesContainer = ref<HTMLDivElement | null>(null);

// Per-message reasoning expander state — tracks which message index is expanded.
const expandedReasonIndex = ref<number | null>(null);

function toggleReason(index: number): void {
  expandedReasonIndex.value = expandedReasonIndex.value === index ? null : index;
}

// One chip per major tool domain (clientes / OS / recebimentos / despesas) so
// the empty state advertises the agent's real breadth, not just the MVP pair.
const suggestedPrompts: string[] = [
  'Quais OS estão em aberto?',
  'Quanto recebemos neste mês?',
  'Quais as maiores despesas do mês?',
  'Quantos clientes ativos temos?',
];

function fillDraft(prompt: string): void {
  // Fill the composer — never auto-send (gotcha #9, plan §4).
  draft.value = prompt;
}

/** Whether the last message is still streaming (SSE stream open). */
const isStreaming = computed<boolean>(() => {
  const last = props.messages[props.messages.length - 1];
  return last?.streaming === true;
});

/** Active tool name from the last streaming message, if any. */
const activeToolName = computed<string | null>(() => {
  const last = props.messages[props.messages.length - 1];
  if (!last?.streaming) return null;
  return last.toolActivity?.name ?? null;
});

/**
 * Returns the display name for a tool call.
 * Non-Full users see the generic label 'ferramenta' (belt-and-suspenders
 * on top of the server-side redaction introduced in A2 — D11).
 * Full users see the real tool name.
 */
function displayToolName(name: string): string {
  return isFull.value ? name : t('ferramenta');
}

function openFranchiseModal(): void {
  eventBus.emit('modal.assistant-franchise.open');
}

/**
 * D7: Notifications are DEFERRED. This is an intentionally inert stub for
 * non-Full/non-Franqueado users who cannot switch franchises.
 * Replace when the notification system lands.
 */
function onRequestTopupStubClick(): void {
  log('AssistantThread', 'onRequestTopupStubClick', 'D7: notificações ao franqueado estão adiadas — stub inerte');
}

/**
 * Sticky-bottom autoscroll.
 * - Opening the sidebar / switching chats / appending a message: ALWAYS snap to
 *   the latest message.
 * - Streaming growth: follow the stream ONLY while the user is already at the
 *   bottom — never yank them down while they scrolled up to read.
 */
const BOTTOM_THRESHOLD_PX = 48;
const isAtBottom = ref<boolean>(true);

function onMessagesScroll(): void {
  const el = messagesContainer.value;
  if (!el) return;
  isAtBottom.value = el.scrollHeight - el.scrollTop - el.clientHeight <= BOTTOM_THRESHOLD_PX;
}

async function scrollToBottom(force: boolean): Promise<void> {
  if (!force && !isAtBottom.value) return;
  await nextTick();
  const el = messagesContainer.value;
  if (el) {
    el.scrollTop = el.scrollHeight;
    isAtBottom.value = true;
  }
  if (force) {
    // Heavy markdown (tables) can grow scrollHeight after first paint —
    // re-snap on the next frame so a forced jump really lands at the bottom.
    requestAnimationFrame(() => {
      const node = messagesContainer.value;
      if (node) {
        node.scrollTop = node.scrollHeight;
      }
    });
  }
}

// Chat switched (covers same-length threads, which the length watcher misses)
// and first load after the sidebar opens: always snap to the latest message.
watch(() => props.activeChatId, () => { void scrollToBottom(true); });

// New message appended (own send, optimistic turn, thread load): always snap.
watch(() => props.messages.length, () => { void scrollToBottom(true); });

// Streaming growth on the active message: stick to bottom only when already
// there. Reacts to a new segment OR growth of the last segment (reason+output).
watch(
  () => {
    const last = props.messages[props.messages.length - 1];
    if (!last?.streaming) return null;
    const segs = last.segments ?? [];
    const lastSeg = segs[segs.length - 1];
    return `${segs.length}:${lastSeg ? lastSeg.text.length : 0}`;
  },
  () => { void scrollToBottom(false); },
);

const inputRef = ref<{ focus: () => void } | null>(null);

/**
 * True from the moment WE submit until the resulting turn settles. Without it,
 * the focus watcher below would also fire for loading transitions this
 * component did not cause — loading a chat, or switching between chats — and
 * steal focus from wherever the user actually was.
 */
const awaitingReply = ref<boolean>(false);

/**
 * Return focus to the field once the turn the user submitted has finished, so
 * they can keep typing without reaching for the mouse. The input is
 * `:disabled` while `loading`, and a disabled field drops focus — which is why
 * it has to be restored rather than simply never lost.
 *
 * nextTick is load-bearing, not cosmetic: `disabled` is still on the element in
 * the tick where `loading` flips to false, and focusing a disabled input is a
 * silent no-op.
 *
 * Only on success. A failed turn leaves its error banner on screen, and pulling
 * the caret back into the field invites the user to retype over a message they
 * have not read yet.
 */
watch(
  () => props.loading,
  (isLoading, wasLoading) => {
    if (isLoading || !wasLoading || !awaitingReply.value) {
      return;
    }
    awaitingReply.value = false;
    if (props.error !== null) {
      return;
    }
    void nextTick(() => {
      inputRef.value?.focus();
    });
  },
);

function submit(): void {
  const text = draft.value.trim();
  if (!text || props.loading || props.activeChatId === null) {
    return;
  }
  awaitingReply.value = true;
  emit('send', text);
  draft.value = '';
}
</script>

<style scoped>
.assistant-thread {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  flex: 1;
  min-width: 0;
  min-height: 0;
}

.assistant-messages {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  flex: 1;
  min-height: 12rem;
  overflow-y: auto;
  /* Never pan the whole thread horizontally — wide content (tables) scrolls
     inside its own markdown block, which has overflow-x:auto. */
  overflow-x: hidden;
  padding: 0.75rem 0.5rem;
}

/* ── Empty state ─────────────────────────────────────────────────────────── */

.assistant-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  /* flex:1 + min-height:0 lets the block truly center in the available messages area
     rather than being pushed up by a fixed min-height on the scroll container. */
  flex: 1;
  min-height: 0;
  padding: 1.5rem 1rem 3rem;
  text-align: center;
}

.assistant-empty-icon {
  font-size: 2rem;
  color: var(--p-primary-color);
  opacity: 0.7;
}

.assistant-empty-greeting {
  font-size: 0.95rem;
  color: var(--p-text-muted-color);
  margin: 0;
}

.assistant-suggestions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  justify-content: center;
}

.assistant-suggestion-chip {
  cursor: pointer;
  user-select: none;
}

/* Pierce PrimeVue's inner .p-chip element to enforce the outlined style.
   PrimeVue renders a filled grey pill by default — override to transparent
   background with a subtle border so chips read as tappable prompts, not tags.
   Tighter horizontal padding (0.5rem) lets two short chips pair on a single row
   even at the narrow 28rem sidebar width, matching the expanded-mode 2-up rhythm. */
.assistant-suggestion-chip :deep(.p-chip) {
  font-size: 0.8rem;
  padding: 0.35rem 0.5rem;
  border: 1px solid var(--p-surface-300, #cbd5e1);
  border-radius: 9999px;
  background: transparent;
  color: var(--p-text-color);
  transition: background 0.15s, border-color 0.15s, color 0.15s;
}

.assistant-suggestion-chip:hover :deep(.p-chip) {
  background: var(--p-surface-50, #f8fafc);
  border-color: var(--p-primary-color);
  color: var(--p-primary-color);
}

/* ── Message bubbles ─────────────────────────────────────────────────────── */

.assistant-bubble-wrapper {
  display: flex;
  flex-direction: column;
  max-width: 85%;
  /* min-width:0 lets max-width actually clamp the wrapper — flex min-width:auto
     would otherwise size it to the widest table cell and overflow the pane. */
  min-width: 0;
}

.assistant-bubble-wrapper-user {
  align-self: flex-end;
  align-items: flex-end;
}

.assistant-bubble-wrapper-assistant {
  align-self: flex-start;
  align-items: flex-start;
  max-width: 100%;
}

.assistant-bubble {
  display: flex;
  padding: 0.6rem 0.9rem;
  border-radius: 1rem;
  word-break: break-word;
  line-height: 1.55;
  min-width: 0;
  max-width: 100%;
}

.assistant-bubble-user {
  background: var(--p-primary-color);
  color: var(--p-primary-contrast-color);
  border-bottom-right-radius: 0.25rem;
}

.assistant-bubble-assistant {
  background: transparent;
  color: var(--p-text-color);
  padding: 0;
  flex-direction: column;
  align-items: stretch;
  gap: 0.5rem;
  border-radius: 0;
}

/* ── Reasoning expander ──────────────────────────────────────────────────── */

.assistant-reason-toggle {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  background: none;
  border: none;
  padding: 0.15rem 0;
  font-size: 0.8rem;
  color: var(--p-text-muted-color);
  cursor: pointer;
  transition: color 0.15s;
}

.assistant-reason-toggle:hover {
  color: var(--p-text-color);
}

.assistant-reason-expanded {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.assistant-reason {
  font-style: italic;
  color: var(--p-text-muted-color);
  font-size: 0.85em;
  white-space: pre-wrap;
  border-left: 2px solid var(--p-surface-400, #cbd5e1);
  padding-left: 0.5rem;
  align-self: stretch;
}

/* ── Output segments ─────────────────────────────────────────────────────── */

.assistant-output-segment {
  align-self: stretch;
}

/* ── Streaming indicators ────────────────────────────────────────────────── */

.assistant-streaming-indicator {
  display: inline-block;
  margin-left: 2px;
  animation: blink 1s step-start infinite;
  color: inherit;
  opacity: 0.7;
}

@keyframes blink {
  50% { opacity: 0; }
}

.assistant-tool-indicator {
  display: block;
  margin-top: 0.25rem;
  font-size: 0.75rem;
  color: var(--p-text-muted-color);
  font-style: italic;
}

/* ── Status line (above composer) ────────────────────────────────────────── */

.assistant-status-line {
  padding: 0.25rem 0.5rem;
  min-height: 1.5rem;
}

.assistant-status-text {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.8rem;
  color: var(--p-text-muted-color);
  font-style: italic;
}

.assistant-status-thinking {
  gap: 0.3rem;
}

.assistant-typing-dot {
  display: inline-block;
  width: 4px;
  height: 4px;
  background: var(--p-text-muted-color);
  border-radius: 50%;
  animation: typing-bounce 1.2s ease-in-out infinite;
}

.assistant-typing-dot:nth-child(2) {
  animation-delay: 0.2s;
}

.assistant-typing-dot:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes typing-bounce {
  0%, 60%, 100% { transform: translateY(0); }
  30% { transform: translateY(-4px); }
}

/* ── 402 Insufficient credits banner ─────────────────────────────────────── */

.assistant-insufficient-banner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.6rem 0.75rem;
  background: var(--p-red-50, #fef2f2);
  border: 1px solid var(--p-red-200, #fecaca);
  border-radius: 0.5rem;
  font-size: 0.85rem;
}

.assistant-insufficient-text {
  color: var(--p-red-700, #b91c1c);
  flex: 1;
}

/* ── Composer ────────────────────────────────────────────────────────────── */

.assistant-input-row {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
}

.assistant-input-field {
  flex: 1;
}

/* ── Assistant markdown rendering ─────────────────────────────────────────── */

/*
 * The v-html target must be display:block with min-width:0 so block-level
 * markdown elements (tables, pre, ul) lay out normally inside the flex bubble.
 * overflow-x:auto lets wide tables scroll instead of blowing out the bubble.
 */
.assistant-bubble-markdown {
  display: block;
  min-width: 0;
  overflow-x: auto;
}

/* Vertical rhythm between markdown blocks — the app reset strips default
   margins, so paragraph/table/heading spacing is set explicitly here. */
:deep(.assistant-bubble-markdown p) {
  margin: 0.65em 0;
}
:deep(.assistant-bubble-markdown table) {
  margin: 0.35em 0 1em;
}
:deep(.assistant-bubble-markdown h1),
:deep(.assistant-bubble-markdown h2),
:deep(.assistant-bubble-markdown h3),
:deep(.assistant-bubble-markdown h4),
:deep(.assistant-bubble-markdown h5),
:deep(.assistant-bubble-markdown h6) {
  margin: 1em 0 0.45em;
  font-weight: 600;
  font-size: 1.05em;
  line-height: 1.3;
}

/* Trim the rhythm at the edges so the segment's own gap/padding is the only
   breathing room at the start and end. */
:deep(.assistant-bubble-markdown > :first-child) {
  margin-top: 0;
}
:deep(.assistant-bubble-markdown > :last-child) {
  margin-bottom: 0;
}

/* Tables — fit the pane by default (cells wrap); a table whose min-content
   width still exceeds the pane scrolls inside its own markdown block. */
:deep(.assistant-bubble-markdown table) {
  border-collapse: collapse;
  width: 100%;
  font-size: 0.875em;
}
:deep(.assistant-bubble-markdown th),
:deep(.assistant-bubble-markdown td) {
  border: 1px solid var(--p-surface-400, #ccc);
  padding: 0.3rem 0.5rem;
  white-space: normal;
  overflow-wrap: anywhere;
}
:deep(.assistant-bubble-markdown th) {
  font-weight: 600;
  background: var(--p-surface-300, #e5e7eb);
}

/* Lists */
:deep(.assistant-bubble-markdown ul),
:deep(.assistant-bubble-markdown ol) {
  padding-left: 1.4rem;
  margin: 0.25rem 0;
}
:deep(.assistant-bubble-markdown li) {
  margin: 0.1rem 0;
}

/* Inline code */
:deep(.assistant-bubble-markdown code) {
  font-family: ui-monospace, monospace;
  font-size: 0.875em;
  background: var(--p-surface-300, #e5e7eb);
  padding: 0.1em 0.3em;
  border-radius: 0.25rem;
}

/* Code blocks */
:deep(.assistant-bubble-markdown pre) {
  background: var(--p-surface-300, #e5e7eb);
  padding: 0.5rem 0.75rem;
  border-radius: 0.375rem;
  overflow-x: auto;
  font-size: 0.875em;
  margin: 0.4rem 0;
}
:deep(.assistant-bubble-markdown pre code) {
  background: none;
  padding: 0;
}

/* Links */
:deep(.assistant-bubble-markdown a) {
  color: var(--p-primary-color);
  text-decoration: underline;
}
</style>
